"use client";

import Link from "next/link";
import cn from "classnames";
import { useRef } from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { FaArrowRight } from "react-icons/fa";
import { motion, useInView } from "framer-motion";

import Button from "@components/ui/form/button";
import { SectionProps } from "@src/types/section";

import SectionBackground from "./section-background";

/** Decorative accent bar above section titles */
const AccentMark = () => (
	<div className="flex items-center gap-3 mb-4">
		<span className="block w-8 h-[2px] bg-accent" />
		<span className="block w-2 h-[2px] bg-accent/40" />
	</div>
);

/** Shared text panel content */
const TextContent = ({
	section,
	padded = true,
	overlay = false,
}: {
	section: SectionProps;
	padded?: boolean;
	overlay?: boolean;
}) => (
	<div
		className={cn(
			"flex flex-col justify-center",
			!overlay && "h-full",
			padded && "p-8 lg:p-12",
			overlay && "[text-shadow:0_2px_12px_rgba(0,0,0,0.5)]"
		)}
	>
		{!section?.hideTitle && (
			<>
				<AccentMark />
				<h2 className={cn("font-medium text-3xl lg:text-5xl mb-5 leading-tight", overlay && "text-white")}>
					{section?.sectionTitle}
				</h2>
			</>
		)}
		{section?.sectionText && (
			<div
				className={cn(
					"prose max-w-none",
					overlay
						? "prose-invert prose-a:text-white prose-p:text-gray-100 prose-p:leading-relaxed max-w-md"
						: "prose-gray prose-headings:text-white prose-a:text-white prose-p:text-gray-400 prose-p:leading-relaxed"
				)}
			>
				{documentToReactComponents(section?.sectionText?.json)}
			</div>
		)}
		{section?.sectionCta && (
			<div className="mt-8">
				<Link
					href={section.sectionCta.url}
					className="group/cta inline-flex items-center gap-3 bg-accent hover:bg-accent-hover text-white px-8 py-3 rounded-lg transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
				>
					<span className="uppercase font-semibold text-sm tracking-wide">{section.sectionCta.label}</span>
					<FaArrowRight className="text-xs group-hover/cta:translate-x-1 transition-transform duration-300" />
				</Link>
			</div>
		)}
	</div>
);

const Section = ({ section, bg = true }: { section: SectionProps; bg?: boolean }) => {
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, margin: "-60px" });

	const hasImages = !!section?.sectionImagesCollection?.items?.length;
	const imageFirst = section?.imagePosition ? true : false;

	return (
		<div
			className={cn("content-container px-4 lg:px-0 my-8", {
				"py-12 lg:py-20": section.margins,
			})}
			ref={ref}
		>
			{hasImages ? (
				<div className="relative flex flex-col overflow-hidden rounded-2xl border border-white/6 min-h-[520px] lg:min-h-[70vh] lg:max-h-[720px]">
					<SectionBackground images={section.sectionImagesCollection!.items!} />

					{/* Scrim: base veil + directional gradient (desktop) + bottom gradient (mobile) */}
					<div className="absolute inset-0 bg-surface/40" />
					<div
						className={cn(
							"absolute inset-0 hidden lg:block",
							imageFirst
								? "bg-linear-to-r from-transparent via-surface/30 to-surface/90"
								: "bg-linear-to-l from-transparent via-surface/30 to-surface/90"
						)}
					/>
					<div className="absolute inset-0 lg:hidden bg-linear-to-t from-surface/95 via-surface/50 to-transparent" />

					{/* Text overlay */}
					<motion.div
						className={cn(
							"relative flex-1 flex flex-col justify-end lg:justify-center p-8 lg:p-14",
							imageFirst ? "items-start lg:items-end" : "items-start"
						)}
						initial={{ opacity: 0, y: 24 }}
						animate={isInView ? { opacity: 1, y: 0 } : {}}
						transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1] }}
					>
						<div className={cn("w-full max-w-xl", imageFirst && "lg:text-right")}>
							<TextContent section={section} padded={false} overlay />
						</div>
					</motion.div>
				</div>
			) : (
				/* Text-only section */
				<motion.div
					className="w-full text-center max-w-3xl mx-auto"
					initial={{ opacity: 0, y: 24 }}
					animate={isInView ? { opacity: 1, y: 0 } : {}}
					transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1] }}
				>
					{!section?.hideTitle && (
						<>
							<div className="flex justify-center">
								<AccentMark />
							</div>
							<h2 className="font-medium text-3xl lg:text-4xl mb-6 leading-tight">{section?.sectionTitle}</h2>
						</>
					)}
					{section?.sectionText && (
						<div
							className={cn(
								"prose prose-gray prose-headings:text-white prose-a:text-white prose-p:text-gray-400 prose-p:leading-relaxed max-w-none",
								{
									"text-center": section?.centerText,
									"text-left": !section?.centerText,
								}
							)}
						>
							{documentToReactComponents(section?.sectionText?.json)}
						</div>
					)}
					{section?.sectionCta && (
						<div className="mt-10">
							<Link href={section.sectionCta.url}>
								<Button className="px-8">
									{section.sectionCta.label} <FaArrowRight />
								</Button>
							</Link>
						</div>
					)}
				</motion.div>
			)}
		</div>
	);
};

export default Section;
